import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,

  CircularProgress,
  Tooltip,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { 
  DataGridPro,
  GridToolbarContainer, 
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExportContainer,
  GridToolbarQuickFilter,
  GridCsvExportMenuItem
} from '@mui/x-data-grid-pro';
import indexedDBService from '../services/indexedDBService';
import { useNavigate } from 'react-router-dom';
import purePromoteService from '../services/purePromoteService';
import { useSnackbar } from 'notistack';

const getProxiedImageUrl = (url) => {
  if (!url || !url.startsWith('https://www.purelocations.com.au/')) {
    return url;
  }
  const baseUrl = 'https://bdm.purelocations.com.au';
  return `${baseUrl}/api/image-proxy?url=${encodeURIComponent(url)}`;
};

const CustomToolbar = ({ selectedRows, onAddToPurePromote }) => {
  return (
    <GridToolbarContainer sx={{ 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%'
    }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarExportContainer>
          <GridCsvExportMenuItem options={{
            fileName: 'locations_export',
            utf8WithBom: true,
            fields: ['name', 'design', 'bookingCount', 'totalRevenue', 'grossProfit', 'conversionRate']
          }} />
        </GridToolbarExportContainer>
        <Tooltip title={selectedRows.length === 0 ? "Select one or more locations to add to Pure Promote" : `Add ${selectedRows.length} location${selectedRows.length === 1 ? '' : 's'} to Pure Promote`}>
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={selectedRows.length === 0}
              onClick={onAddToPurePromote}
              sx={{ 
                ml: 2,
                backgroundColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                  cursor: 'not-allowed'
                }
              }}
            >
              Add to Pure Promote {selectedRows.length > 0 && `(${selectedRows.length})`}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ minWidth: '320px' }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
};

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const savedStartDate = localStorage.getItem('globalStartDate');
    const savedEndDate = localStorage.getItem('globalEndDate');
    return { 
      start: savedStartDate ? new Date(savedStartDate) : new Date(new Date().setDate(new Date().getDate() - 30)),
      end: savedEndDate ? new Date(savedEndDate) : new Date()
    };
  });
  const [atRiskLocations, setAtRiskLocations] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [purePromoteLocations, setPurePromoteLocations] = useState([]);
  const [showPurePromoteDialog, setShowPurePromoteDialog] = useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleGlobalDateChange = (event) => {
      const { startDate, endDate } = event.detail;
      const newStartDate = new Date(startDate);
      const newEndDate = new Date(endDate);
      setDateRange({ 
        start: newStartDate, 
        end: newEndDate 
      });
    };

    window.addEventListener('globalDateChange', handleGlobalDateChange);
    return () => {
      window.removeEventListener('globalDateChange', handleGlobalDateChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('globalStartDate', dateRange.start.toISOString());
    localStorage.setItem('globalEndDate', dateRange.end.toISOString());
  }, [dateRange]);

  useEffect(() => {
    const loadLocations = async () => {
      setIsLoading(true);
      try {
        const [locations, clients] = await Promise.all([
          indexedDBService.getAllLocations(),
          indexedDBService.getAllClients()
        ]);

        const startDate = new Date(dateRange.start);
        const endDate = new Date(dateRange.end);

        // Calculate the previous period
        const periodLength = endDate.getTime() - startDate.getTime();
        const previousPeriodStart = new Date(startDate.getTime() - periodLength);
        const previousPeriodEnd = startDate;

        const locationStats = new Map();

        // Initialize stats for each location
        locations.forEach(location => {
          locationStats.set(location.name, {
            ...location,
            bookingCount: 0,
            totalRevenue: 0,
            previousPeriodRevenue: 0,
            previousPeriodBookings: 0,
            potentialBookings: 0,
            previousPeriodPotentialBookings: 0,
            grossProfit: 0,
            previousPeriodGrossProfit: 0,
            Website_Feature_Image_URL__c: location.Website_Feature_Image_URL__c
          });

          const stats = locationStats.get(location.name);

          // Initialize shoot/event specific stats
          stats.shootBookingCount = 0;
          stats.shootPotentialBookings = 0;
          stats.eventBookingCount = 0;
          stats.eventPotentialBookings = 0;
          
          // Process the potentialLocations to categorize by record type
          location.potentialLocations?.forEach(pl => {
            const createdDate = new Date(pl.CreatedDate);
            if (createdDate >= startDate && createdDate <= endDate) {
              if (pl.Opportunity__r?.Name) {
                stats.potentialBookings++;
                
                // Categorize by record type
                if (pl.Opportunity__r?.RecordTypeId === '0125g0000005zPHAAY') {
                  stats.shootPotentialBookings++;
                } else if (pl.Opportunity__r?.RecordTypeId === '0125g0000005zPCAAY') {
                  stats.eventPotentialBookings++;
                }
              }
            }
            // Calculate potential bookings for previous period
            else if (createdDate >= previousPeriodStart && createdDate < previousPeriodEnd) {
              if (pl.Opportunity__r?.Name) {
                stats.previousPeriodPotentialBookings++;
              }
            }
          });
        });

        // Process opportunities for both periods
        clients.forEach(client => {
          const processedOpps = new Map();

          client.opportunities?.forEach(opp => {
            const closeDate = new Date(opp.CloseDate);
            const locationName = opp.Location_Name__r?.Name;
            
            if (locationName && locationStats.has(locationName)) {
              if (!processedOpps.has(locationName)) {
                processedOpps.set(locationName, new Set());
              }

              if (!processedOpps.get(locationName).has(opp.Name)) {
                const stats = locationStats.get(locationName);
                
                // Current period
                if (closeDate >= startDate && closeDate <= endDate) {
                  if (opp.StageName === 'Closed Won') {
                    stats.bookingCount++;
                    stats.totalRevenue += Number(opp.Total_Hire_Fees__c) || 0;
                    stats.grossProfit += Number(opp.Gross_Profit__c) || 0;
                    
                    // Categorize by record type
                    if (opp.RecordTypeId === '0125g0000005zPHAAY') {
                      stats.shootBookingCount++;
                    } else if (opp.RecordTypeId === '0125g0000005zPCAAY') {
                      stats.eventBookingCount++;
                    }
                  }
                }
                // Previous period
                else if (closeDate >= previousPeriodStart && closeDate < previousPeriodEnd) {
                  if (opp.StageName === 'Closed Won') {
                    stats.previousPeriodBookings++;
                    stats.previousPeriodRevenue += Number(opp.Total_Hire_Fees__c) || 0;
                    stats.previousPeriodGrossProfit += Number(opp.Gross_Profit__c) || 0;
                  }
                }
                
                processedOpps.get(locationName).add(opp.Name);
              }
            }
          });
        });

        // Calculate conversion rates and trends
        locationStats.forEach(stats => {
          // Overall conversion rate (keep this for compatibility)
          stats.conversionRate = stats.potentialBookings > 0 
            ? (stats.bookingCount / stats.potentialBookings) * 100 
            : 0;
          
          // Shoot conversion rate
          stats.shootConversionRate = stats.shootPotentialBookings > 0
            ? (stats.shootBookingCount / stats.shootPotentialBookings) * 100
            : 0;
          
          // Event conversion rate
          stats.eventConversionRate = stats.eventPotentialBookings > 0
            ? (stats.eventBookingCount / stats.eventPotentialBookings) * 100
            : 0;

          // Revenue trend: percentage change from previous period
          stats.trend = stats.previousPeriodRevenue > 0
            ? ((stats.totalRevenue - stats.previousPeriodRevenue) / stats.previousPeriodRevenue) * 100
            : 0;
        });

        if (startDate && endDate) {
          // Calculate the previous period
          const periodLength = endDate.getTime() - startDate.getTime();
          const previousPeriodStart = new Date(startDate.getTime() - periodLength);
          const previousPeriodEnd = startDate;

          const riskLocations = Array.from(locationStats.values())
            .filter(location => {
              // Only include locations that had any revenue in either period
              return location.totalRevenue > 0 || location.previousPeriodRevenue > 0;
            })
            .map(location => {
              // Calculate revenue drop using the correct period values
              const currentPeriodRevenue = location.totalRevenue || 0;
              const previousPeriodRevenue = location.previousPeriodRevenue || 0;

              const revenueDrop = previousPeriodRevenue > 0 ? 
                ((previousPeriodRevenue - currentPeriodRevenue) / previousPeriodRevenue) * 100 : 0;

              return {
                ...location,
                revenueDrop: revenueDrop || 0,
                revenueDropValue: (previousPeriodRevenue - currentPeriodRevenue) || 0,
                // Add period information for debugging if needed
                currentPeriodStart: startDate,
                currentPeriodEnd: endDate,
                previousPeriodStart: previousPeriodStart,
                previousPeriodEnd: previousPeriodEnd
              };
            })
            .filter(location => (
              // Only include locations with positive revenue drop and previous period revenue
              location.revenueDrop > 0 && 
              location.previousPeriodRevenue > 0
            ))
            .sort((a, b) => (b.revenueDropValue || 0) - (a.revenueDropValue || 0))
            .slice(0, 20);

          setAtRiskLocations(riskLocations);
        }

        setLocations(Array.from(locationStats.values()));
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLocations();
  }, [dateRange]);

  const columns = [
    {
      field: 'name',
      headerName: 'Location Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            width: '100%'
          }}
          onClick={() => navigate(`/locations/${params.row.accountId}`, {
            state: { location: params.row }
          })}
        >
          {params.row.Website_Feature_Image_URL__c && (
            <Box
              component="img"
              src={getProxiedImageUrl(params.row.Website_Feature_Image_URL__c)}
              alt={params.value}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                objectFit: 'cover'
              }}
            />
          )}
          <Box
            sx={{
              color: 'primary.main',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            {params.value}
          </Box>
        </Box>
      ),
    },
    {
      field: 'address',
      headerName: 'Address',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return params?.row?.address || 'N/A';
      },
    },

    {
      field: 'design',
      headerName: 'Design Style',
      flex: 0.5,
      minWidth: 200,
      hide: true,
    },
    {
      field: 'bookingCount',
      headerName: 'Total Bookings',
      type: 'number',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        const previousValue = params.row.previousPeriodBookings || 0;
        const color = currentValue > previousValue ? '#4caf50' : 
                      currentValue < previousValue ? '#f44336' : '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Current Period: {currentValue}
                </Typography>
                <Typography variant="body2">
                  Previous Period: {previousValue}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              {currentValue.toLocaleString()}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'potentialBookings',
      headerName: 'Potential Bookings',
      type: 'number',
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        const previousValue = params.row.previousPeriodPotentialBookings || 0;
        const color = currentValue > previousValue ? '#4caf50' : 
                      currentValue < previousValue ? '#f44336' : '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Current Period: {currentValue}
                </Typography>
                <Typography variant="body2">
                  Previous Period: {previousValue}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              {currentValue.toLocaleString()}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'totalRevenue',
      headerName: 'Total Revenue',
      type: 'number',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        const previousValue = params.row.previousPeriodRevenue || 0;
        const color = currentValue > previousValue ? '#4caf50' : 
                      currentValue < previousValue ? '#f44336' : '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Current Period: ${currentValue.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Previous Period: ${previousValue.toLocaleString()}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              ${currentValue.toLocaleString()}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'grossProfit',
      headerName: 'Gross Profit',
      type: 'number',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        const previousValue = params.row.previousPeriodGrossProfit || 0;
        const color = currentValue > previousValue ? '#4caf50' : 
                      currentValue < previousValue ? '#f44336' : '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Current Period: ${currentValue.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Previous Period: ${previousValue.toLocaleString()}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              ${currentValue.toLocaleString()}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'shootConversionRate',
      headerName: 'Shoot Conversion',
      type: 'number',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        // We don't have previous period data for this yet, so we'll just show the current value
        const color = '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Shoot Bookings: {params.row.shootBookingCount || 0}
                </Typography>
                <Typography variant="body2">
                  Potential Shoots: {params.row.shootPotentialBookings || 0}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              {currentValue.toFixed(1)}%
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'eventConversionRate',
      headerName: 'Event Conversion',
      type: 'number',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        // We don't have previous period data for this yet, so we'll just show the current value
        const color = '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Event Bookings: {params.row.eventBookingCount || 0}
                </Typography>
                <Typography variant="body2">
                  Potential Events: {params.row.eventPotentialBookings || 0}
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              {currentValue.toFixed(1)}%
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'conversionRate',
      headerName: 'Overall Conversion',
      type: 'number',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const currentValue = params.value || 0;
        // Calculate previous period conversion rate
        const previousPotential = params.row.previousPeriodPotentialBookings || 1; // Avoid division by zero
        const previousBookings = params.row.previousPeriodBookings || 0;
        const previousRate = (previousBookings / previousPotential) * 100;
        
        const color = currentValue > previousRate ? '#4caf50' : 
                      currentValue < previousRate ? '#f44336' : '#757575';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Current Period: {currentValue.toFixed(1)}%
                </Typography>
                <Typography variant="body2">
                  Previous Period: {previousRate.toFixed(1)}%
                </Typography>
              </Box>
            }
            arrow
          >
            <Box sx={{ color }}>
              {currentValue.toFixed(1)}%
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'trend',
      headerName: 'Revenue Trend',
      type: 'number',
      flex: 0.5,
      minWidth: 120,
      renderCell: (params) => {
        const value = params.value || 0;
        const color = value > 0 ? '#4caf50' : value < 0 ? '#f44336' : '#757575';
        const arrow = value > 0 ? <TrendingUpIcon fontSize="small" /> : value < 0 ? <TrendingDownIcon fontSize="small" /> : '→';
        
        return (
          <Tooltip
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="body2">
                  Current Period: ${params.row.totalRevenue.toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  Previous Period: ${params.row.previousPeriodRevenue.toLocaleString()}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" sx={{ color }}>
                  {value > 0 ? 'Increase' : value < 0 ? 'Decrease' : 'No Change'}: {Math.abs(value).toFixed(1)}%
                </Typography>
              </Box>
            }
            arrow
          >
            <Box
              sx={{
                color,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {arrow} {Math.abs(value).toFixed(1)}%
            </Box>
          </Tooltip>
        );
      },
    },
    {
        field: 'status',
        headerName: 'Status',
        flex: 0.5,
        minWidth: 100,
        renderCell: (params) => (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Box
              sx={{
                backgroundColor: params.value === 'Active Location' ? '#4caf50' : '#ff9800',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '50px',
                fontSize: '0.75rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '24px',
              }}
            >
              {params.value === 'Active Location' ? 'Active' : params.value || 'N/A'}
            </Box>
          </Box>
        ),
      },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => {
        const handleClick = () => {
          // Get the Salesforce base URL from environment variable or use a default
          const sfBaseUrl = process.env.REACT_APP_SALESFORCE_URL || 'https://purelocations.lightning.force.com';
          const accountId = params.row.accountId;
          
          // Construct and open the Salesforce URL
          const sfUrl = `${sfBaseUrl}/lightning/r/Account/${accountId}/view`;
          window.open(sfUrl, '_blank');
        };

        return (
          <IconButton 
            size="small" 
            onClick={handleClick}
            title="Open in Salesforce"
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        );
      },
    }
  ];

  const filteredLocations = locations.filter(location => {
    const searchRegex = new RegExp(searchTerm, 'i');
    return (
      searchRegex.test(location.name) ||
      searchRegex.test(location.address) ||
      searchRegex.test(location.design)
    );
  });

  // Handler for adding selected locations to Pure Promote
  const handleAddToPurePromote = () => {
    // Filter out any locations that are already in the Pure Promote list
    const newLocations = selectedRows.filter(
      selectedLocation => !purePromoteLocations.some(
        existing => existing.name === selectedLocation.name
      )
    );
    
    setPurePromoteLocations(prev => [...prev, ...newLocations]);
  };

  // Handler for selection change
  const handleSelectionChange = (newSelection) => {
    const selectedLocationData = filteredLocations.filter(
      location => newSelection.includes(location.name)
    );
    setSelectedRows(selectedLocationData);
  };

  // Pure Promote columns configuration
  const purePromoteColumns = [
    {
      field: 'name',
      headerName: 'Location Name',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => {
            setPurePromoteLocations(prev => 
              prev.filter(loc => loc.name !== params.row.name)
            );
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    }
  ];

  const handleGenerateMultiLocationReport = async () => {
    if (purePromoteLocations.length === 0) return;
    setShowPurePromoteDialog(true);
  };

  const handleDownloadReport = async () => {
    try {
      setIsLoading(true);
      // Just generate and download the Excel report without storing anything
      await purePromoteService.generateMultiLocationReport(purePromoteLocations);
      setShowPurePromoteDialog(false);
    } catch (error) {
      console.error('Error generating multi-location Pure Promote report:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendToPurePromote = async () => {
    try {
      setIsLoading(true);

      // Clear existing data from localStorage and IndexedDB
      // console.log('Clearing existing Pure Promote data...');
      
      // Clear IndexedDB data
      await Promise.all([
        indexedDBService.clearPurePromoteReports(),
        indexedDBService.clearPurePromoteContacts()
      ]);
      
      // Clear any localStorage data related to Pure Promote
      localStorage.removeItem('purePromoteState');
      localStorage.removeItem('purePromoteFilters');
      localStorage.removeItem('purePromoteSelection');
      
      // console.log('Existing data cleared successfully');

      // First fetch data for all selected locations
      // console.log('Fetching data for locations:', purePromoteLocations.map(loc => `${loc.name} (${loc.accountId})`));
      
      const locationsData = await Promise.all(
        purePromoteLocations.map(location => 
          purePromoteService.fetchLocationData(location.accountId, location.name)
        )
      );
      
      // Get the report data by combining the locations data
      const reportData = purePromoteService.combineLocationsData(locationsData, purePromoteLocations);
      
      // Check if we have any actual data
      if (reportData.__metadata && !reportData.__metadata.hasData) {
        console.warn('No data found for selected locations:', reportData.__metadata);
        
        // Create a more specific message based on location count
        let message = '';
        if (purePromoteLocations.length === 1) {
          message = `No contacts found for location "${purePromoteLocations[0].name}". This location may not have any closed opportunities in the last 2 years.`;
        } else {
          message = `No contacts found for the ${purePromoteLocations.length} selected locations. These locations may not have any closed opportunities in the last 2 years.`;
        }
        
        enqueueSnackbar(message, { 
          variant: 'warning',
          autoHideDuration: 8000 
        });
        
        // Still store the empty data and navigate - the PurePromote page will handle showing appropriate messaging
        purePromoteService.setReportData(reportData);
        
        // Save empty report to IndexedDB with location IDs for future reference
        await indexedDBService.savePurePromoteReport({
          data: reportData,
          locationId: purePromoteLocations.map(loc => loc.accountId).join(',')
        });
        
        navigate('/pure-promote');
        return;
      }
      
      // Store the data in both the service and IndexedDB since we're using Pure Promote
      purePromoteService.setReportData(reportData);
      
      // Save report to IndexedDB with location IDs for future reference
      await indexedDBService.savePurePromoteReport({
        data: reportData,
        locationId: purePromoteLocations.map(loc => loc.accountId).join(',')
      });
      
      navigate('/pure-promote');
    } catch (error) {
      console.error('Error preparing Pure Promote data:', error);
      enqueueSnackbar('Error preparing Pure Promote data: ' + (error.message || 'Unknown error'), { 
        variant: 'error',
        autoHideDuration: 8000 
      });
    } finally {
      setIsLoading(false);
      setShowPurePromoteDialog(false);
    }
  };

  return (
    <Box>
      <Grid container spacing={3} sx={{ height: 'calc(100vh - 88px)', overflow: 'hidden' }}>
        {/* Main locations grid */}
        <Grid item xs={12} md={10} sx={{ height: '100%' }}>
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: '#FFFFFF',
            padding: 2,
            borderRadius: 1,
            boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{ 
              flex: 1,
              overflow: 'hidden'
            }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGridPro
                  rows={filteredLocations}
                  columns={columns}
                  density="comfortable"
                  pagination
                  checkboxSelection
                  disableRowSelectionOnClick
                  getRowId={(row) => row.name}
                  slots={{ 
                    toolbar: CustomToolbar 
                  }}
                  slotProps={{
                    toolbar: {
                      selectedRows: selectedRows,
                      onAddToPurePromote: handleAddToPurePromote,
                      printOptions: { 
                        hideFooter: true,
                        hideToolbar: true,
                      },
                      csvOptions: { 
                        fileName: 'locations_export',
                        utf8WithBom: true,
                      },
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 }
                    }
                  }}
                  onRowSelectionModelChange={handleSelectionChange}
                  getContextMenuItems={(params) => {
                    const items = [];
                    
                    // Only show "Add to Pure Promote" if rows are selected
                    if (selectedRows.length > 0) {
                      items.push({
                        label: 'Add to Pure Promote',
                        icon: <AddIcon fontSize="small" />,
                        action: handleAddToPurePromote
                      });
                    }
                    
                    return items;
                  }}
                  sx={{
                    height: '100%',
                    '& .MuiDataGrid-main': { overflow: 'hidden' },
                    // Add zebra striping
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                      backgroundColor: 'rgba(255, 255, 255, 0)',
                    },
                    // Hover effect
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                    // Header styling
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: '#ffffff',
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    },
                    // Add this for the top container row
                    '& .MuiDataGrid-container--top [role=row]': {
                      backgroundColor: '#ffffff',
                    },
                    '& .MuiDataGrid-row': {
                      backgroundColor: '#FFFFFFBC',
                    },
                    // Selected row
                    '& .MuiDataGrid-row.Mui-selected': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.12)',
                      },
                    },
                    // Footer styling
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: '1px solid rgba(224, 224, 224, 1)',
                      backgroundColor: 'rgba(255, 255, 255, 1)',
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                    // Density styles (already present)
                    '& .MuiDataGrid-row': {
                      '&.MuiDataGrid-row--densityCompact': {
                        maxHeight: '32px',
                      },
                      '&.MuiDataGrid-row--densityStandard': {
                        maxHeight: '52px',
                      },
                      '&.MuiDataGrid-row--densityComfortable': {
                        maxHeight: '64px',
                      },
                    }
                  }}
                  initialState={{
                    pagination: { pageSize: 25 },
                    sorting: {
                      sortModel: [{ field: 'bookingCount', sort: 'desc' }],
                    }
                  }}
                />
              )}
            </Box>
          </Box>
        </Grid>

        {/* Right Panel with Risk and Pure Promote */}
        <Grid item xs={12} md={2} sx={{ height: '100%' }}>
          <Box sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 2 
          }}>
            {/* Top 20 Locations at Risk */}
            <Card sx={{ height: '50%', overflow: 'hidden' }}>
              <CardContent sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 0,
                '&:last-child': { pb: 0 }
              }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="p" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" />
                    Top 20 Locations at Risk
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  p: 2
                }}>
                  {atRiskLocations.map((location) => (
                    <Box key={location.accountId} sx={{ mb: 2 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          cursor: 'pointer',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                        onClick={() => navigate(`/locations/${location.accountId}`, {
                          state: { location: location }
                        })}
                      >
                        {location.name}
                      </Typography>
                      <Typography color="error" variant="caption" component="div">
                        Revenue drop: ${(location.revenueDropValue || 0).toLocaleString()}
                      </Typography>
                      <Typography color="error" variant="caption" component="div">
                        ({(location.revenueDrop || 0).toFixed(1)}% decrease)
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                        Previous: ${(location.previousPeriodRevenue || 0).toLocaleString()} → 
                        Current: ${(location.totalRevenue || 0).toLocaleString()}
                      </Typography>
                      <Divider sx={{ my: 2, borderColor: '#f4f4f4' }} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Pure Promote Widget */}
            <Card sx={{ height: '50%', overflow: 'hidden' }}>
              <CardContent sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                p: 0,
                '&:last-child': { pb: 0 }
              }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="p" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CampaignIcon sx={{ color: 'success.main' }} />
                    Pure Promote
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  flex: 1, 
                  overflowY: 'auto', // Enable vertical scrolling
                }}>
                  <DataGridPro
                    rows={purePromoteLocations}
                    columns={purePromoteColumns}
                    getRowId={(row) => row.name}
                    hideFooter
                    density="compact"
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid rgba(224, 224, 224, 0.4)'
                      }
                    }}
                  />
                </Box>
                <Box sx={{ 
                  p: 2, 
                  borderTop: 1, 
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  alignItems: 'center'
                }}>
                  <Typography variant="caption" color="text.secondary">
                    {purePromoteLocations.length} location{purePromoteLocations.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<DescriptionIcon />}
                    onClick={handleGenerateMultiLocationReport}
                    disabled={purePromoteLocations.length === 0}
                  >
                    Generate Report
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Add the dialog */}
      <Dialog
        open={showPurePromoteDialog}
        onClose={() => setShowPurePromoteDialog(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="pure-promote-dialog-title"
        disablePortal
        keepMounted={false}
      >
        <DialogTitle id="pure-promote-dialog-title">Pure Promote Options</DialogTitle>
        <DialogContent>
          <Typography>
            Choose how you would like to view the Pure Promote report for {purePromoteLocations.length} location(s):
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleDownloadReport}
            startIcon={<DescriptionIcon />}
            tabIndex={0}
          >
            Download Excel Report
          </Button>
          <Button
            variant="contained"
            onClick={handleSendToPurePromote}
            startIcon={<CampaignIcon />}
            tabIndex={0}
            autoFocus
          >
            Open in Pure Promote
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Locations; 